/**
 * Mural Calculator - Storefront JavaScript
 * Integrates with the Mural Calculator Shopify App via App Proxy
 * Supports both Panel and Yardage product types
 */

(function() {
  'use strict';

  // ============================================
  // Configuration
  // ============================================
  const CONFIG = {
    MAX_WALLS: 8,
    MIN_PANEL_HEIGHT: 36,
    STORAGE_PREFIX: 'mc_',
    RETRY_ATTEMPTS: 3,
    RETRY_DELAY: 1000,
    // Yardage-specific constants
    YARDAGE_INSTALLATION_BUFFER: 6,    // 6" installation allowance
    YARDAGE_MAX_YARDS_PER_BOLT: 45,    // Maximum yards per bolt
    YARDAGE_MIN_ORDER_YARDS: 10        // Minimum order requirement
  };

  // ============================================
  // Initialization
  // ============================================
  const root = document.getElementById('mural-calculator');
  if (!root) return;

  // Get configuration from data attributes
  // Determine calculator mode from data attribute
  const calculatorMode = root.dataset.calculatorMode || 'panel';

  const config = {
    // Calculator mode: 'panel' or 'yardage'
    mode: calculatorMode,
    // Common fields
    productId: root.dataset.productId,
    productHandle: root.dataset.productHandle,
    productTitle: root.dataset.productTitle,
    variantId: root.dataset.variantId,
    variantSku: root.dataset.variantSku,
    isPanelVariant: root.dataset.isPanelVariant === 'true',
    isYardVariant: root.dataset.isYardVariant === 'true',
    isSampleVariant: root.dataset.isSampleVariant === 'true',
    currentMaterialCode: root.dataset.currentMaterialCode || null,
    panelVariants: JSON.parse(root.dataset.panelVariants || '[]'),
    yardVariants: JSON.parse(root.dataset.yardVariants || '[]'),
    // Panel-specific fields
    panelWidth: parseFloat(root.dataset.panelWidth) || 0,
    maxHeight: parseInt(root.dataset.maxHeight) || 810,
    minHeight: parseInt(root.dataset.minHeight) || 36,
    bleed: parseInt(root.dataset.bleed) || 6,
    repeat: (root.dataset.repeat || 'AB').toUpperCase(),
    // Yardage-specific fields
    yardageWidth: parseFloat(root.dataset.yardageWidth) || 0,
    repeatHeight: parseFloat(root.dataset.repeatHeight) || 0,
    // Product spec metafields
    productionWidth: root.dataset.productionWidth || '',
    installationType: root.dataset.installationType || '',
    // API and auth
    apiEndpoint: root.dataset.apiEndpoint || '/apps/mural-calc/api/quote/submit',
    isLoggedIn: root.dataset.loggedIn === 'true',
    customerId: root.dataset.customerId || null,
    customerEmail: root.dataset.customerEmail || null,
    customerName: root.dataset.customerName || null,
    loginUrl: root.dataset.loginUrl || '/account/login'
  };

  // Validate configuration based on mode
  if (config.mode === 'panel') {
    if (config.panelWidth <= 0) {
      console.error('Mural Calculator: Invalid panel width');
      return;
    }
  } else if (config.mode === 'yardage') {
    if (config.yardageWidth <= 0 || config.repeatHeight <= 0) {
      console.error('Mural Calculator: Invalid yardage width or repeat height');
      return;
    }
  } else {
    console.error('Mural Calculator: Unknown mode', config.mode);
    return;
  }

  // ============================================
  // State Management
  // ============================================
  const state = {
    walls: [],
    wallCounter: 0,
    confirmedWalls: new Set(),
    totals: null,
    // Panel overage: { type: 'standard'|'custom'|'none', customPanels: { A, B, C } }
    // Yardage overage: { type: 'standard'|'custom'|'none', customYards: 0 }
    overage: config.mode === 'yardage'
      ? { type: 'standard', customYards: 0 }
      : { type: 'standard', customPanels: { A: 0, B: 0, C: 0 } },
    cfa: { type: null },
    projectDetails: {
      sidemark: '',
      projectName: '',
      previousOrder: '',
      notes: ''
    },
    isSubmitting: false
  };

  // ============================================
  // DOM References
  // ============================================
  const dom = {
    accordion: root.querySelector('.mc-accordion__trigger'),
    accordionContent: root.querySelector('.mc-accordion__content'),
    loginBanner: root.querySelector('[data-mc-login-banner]'),
    form: root.querySelector('[data-mc-form]'),
    // Material selection elements
    materialRequired: root.querySelector('[data-mc-material-required]'),
    materialSelector: root.querySelector('[data-mc-material-selector]'),
    currentMaterial: root.querySelector('[data-mc-current-material]'),
    changeMaterialBtn: root.querySelector('[data-mc-change-material]'),
    // Wall elements
    wallsSection: root.querySelector('[data-mc-walls-section]'),
    wallsContainer: root.querySelector('[data-mc-walls]'),
    wallTemplate: root.querySelector('#mc-wall-template'),
    addWallBtn: root.querySelector('[data-mc-add-wall]'),
    summarySection: root.querySelector('[data-mc-summary-section]'),
    overageSection: root.querySelector('[data-mc-overage-section]'),
    cfaSection: root.querySelector('[data-mc-cfa-section]'),
    projectSection: root.querySelector('[data-mc-project-section]'),
    submitSection: root.querySelector('[data-mc-submit-section]'),
    submitBtn: root.querySelector('[data-mc-submit]'),
    errorContainer: root.querySelector('[data-mc-error]'),
    errorMessage: root.querySelector('[data-mc-error-message]'),
    successContainer: root.querySelector('[data-mc-success]'),
    orderName: root.querySelector('[data-mc-order-name]'),
    modal: root.querySelector('[data-mc-modal]'),
    modalTitle: root.querySelector('[data-mc-modal-title]'),
    modalBody: root.querySelector('[data-mc-modal-body]'),
    // Success modal
    successModal: root.querySelector('[data-mc-success-modal]'),
    successModalOrderName: root.querySelector('[data-mc-modal-order-name]')
  };

  // ============================================
  // Utility Functions
  // ============================================
  const utils = {
    num: (v) => {
      const n = Number(String(v).replace(/[^\d.-]/g, ''));
      return Number.isFinite(n) ? n : 0;
    },

    ceil: Math.ceil,

    delay: (ms) => new Promise(resolve => setTimeout(resolve, ms)),

    show: (el) => { if (el) el.style.display = ''; },
    hide: (el) => { if (el) el.style.display = 'none'; },

    addClass: (el, cls) => { if (el) el.classList.add(cls); },
    removeClass: (el, cls) => { if (el) el.classList.remove(cls); },

    formatFeetInches: (inches) => {
      const ft = Math.floor(inches / 12);
      const ins = inches % 12;
      if (ins === 0) return `${ft}'`;
      return `${ft}'${ins}"`;
    },

    feetInchesToInches: (ft, inches) => {
      return (utils.num(ft) * 12) + utils.num(inches);
    },

    // Validate US ZIP code (5 digits or 5+4 format)
    isValidZipCode: (zip) => {
      const cleaned = zip.replace(/\s/g, '');
      return /^\d{5}(-\d{4})?$/.test(cleaned);
    },

    // Validate email address
    isValidEmail: (email) => {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },

    // Escape HTML to prevent XSS (used for text content)
    escapeHtml: (str) => {
      if (typeof str !== 'string') return '';
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    },

    // Safe rendering: renders messages to a container using DOM methods (prevents XSS)
    renderMessages: (container, messages, className = '') => {
      if (!container) return;
      container.innerHTML = ''; // Clear safely
      messages.forEach(msg => {
        const div = document.createElement('div');
        div.textContent = msg; // Safe - escapes HTML
        if (className) div.className = className;
        container.appendChild(div);
      });
    },

    // Safe template rendering: clones content from a template element
    renderTemplateContent: (templateId, targetContainer) => {
      const template = document.getElementById(templateId);
      if (!template || !template.content) return false;

      targetContainer.innerHTML = ''; // Clear first
      const fragment = document.importNode(template.content, true);
      targetContainer.appendChild(fragment);
      return true;
    }
  };

  // ============================================
  // CFA Validation Functions
  // ============================================
  function validateZipCode() {
    const postalInput = root.querySelector('[data-mc-cfa-postal]');
    const postalError = root.querySelector('[data-mc-cfa-postal-error]');

    if (!postalInput || !postalInput.value.trim()) {
      return true; // Let required validation handle empty
    }

    const isValid = utils.isValidZipCode(postalInput.value.trim());

    if (!isValid) {
      postalInput.classList.add('mc-field__input--error');
      if (postalError) {
        postalError.textContent = 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)';
        utils.show(postalError);
      }
    } else {
      postalInput.classList.remove('mc-field__input--error');
      if (postalError) utils.hide(postalError);
    }

    return isValid;
  }

  function validateCfaEmail() {
    const emailInput = root.querySelector('[data-mc-cfa-email]');
    const emailError = root.querySelector('[data-mc-cfa-email-error]');

    if (!emailInput || !emailInput.value.trim()) {
      return true; // Let required validation handle empty
    }

    const isValid = utils.isValidEmail(emailInput.value.trim());

    if (!isValid) {
      emailInput.classList.add('mc-field__input--error');
      if (emailError) utils.show(emailError);
    } else {
      emailInput.classList.remove('mc-field__input--error');
      if (emailError) utils.hide(emailError);
    }

    return isValid;
  }

  // ============================================
  // Session Storage Persistence (PRD FR-1.8)
  // ============================================
  const storage = {
    key: `${CONFIG.STORAGE_PREFIX}${config.productHandle}`,

    save() {
      try {
        const data = {
          version: '2.0',
          timestamp: new Date().toISOString(),
          walls: state.walls.map(w => ({
            id: w.id,
            number: w.number,
            heightFt: w.heightFt,
            heightIn: w.heightIn,
            widthFt: w.widthFt,
            widthIn: w.widthIn,
            confirmed: w.confirmed,
            continuesFromPrevious: w.continuesFromPrevious
          })),
          wallCounter: state.wallCounter,
          overage: state.overage,
          cfa: state.cfa,
          projectDetails: state.projectDetails,
          accordionOpen: dom.accordion?.getAttribute('aria-expanded') === 'true'
        };
        sessionStorage.setItem(this.key, JSON.stringify(data));
      } catch (e) {
        console.warn('Failed to save calculator state:', e);
      }
    },

    load() {
      try {
        const json = sessionStorage.getItem(this.key);
        if (!json) return null;

        const data = JSON.parse(json);

        // Check if data is recent (within session)
        const timestamp = new Date(data.timestamp);
        const age = Date.now() - timestamp.getTime();
        const maxAge = 60 * 60 * 1000; // 1 hour

        if (age > maxAge) {
          this.clear();
          return null;
        }

        return data;
      } catch (e) {
        console.warn('Failed to load calculator state:', e);
        return null;
      }
    },

    clear() {
      try {
        sessionStorage.removeItem(this.key);
      } catch (e) {
        console.warn('Failed to clear calculator state:', e);
      }
    }
  };

  // ============================================
  // Calculation Engine
  // ============================================
  const calculator = {
    calculateWall(widthInches, heightInches) {
      if (widthInches <= 0 || heightInches <= 0) return null;

      const panelHeight = heightInches + config.bleed;
      const panels = utils.ceil(widthInches / config.panelWidth);
      const sqFtPerPanel = utils.ceil((config.panelWidth * panelHeight) / 144);

      return {
        panels,
        panelHeight,
        sqFtPerPanel,
        totalSqFt: panels * sqFtPerPanel
      };
    },

    generateSequence(panelCount, startLetter = 'A') {
      const letters = config.repeat === 'ABC' ? ['A', 'B', 'C'] : config.repeat === 'A' ? ['A'] : ['A', 'B'];
      const startIndex = letters.indexOf(startLetter);
      const sequence = [];

      for (let i = 0; i < panelCount; i++) {
        sequence.push(letters[(startIndex + i) % letters.length]);
      }

      return sequence;
    },

    getNextLetter(sequence) {
      if (!sequence || sequence.length === 0) return 'A';
      const letters = config.repeat === 'ABC' ? ['A', 'B', 'C'] : config.repeat === 'A' ? ['A'] : ['A', 'B'];
      const lastLetter = sequence[sequence.length - 1];
      const lastIndex = letters.indexOf(lastLetter);
      return letters[(lastIndex + 1) % letters.length];
    },

    calculateTotals() {
      const confirmedWalls = state.walls.filter(w => w.confirmed);
      if (confirmedWalls.length === 0) return null;

      // Group walls by connection
      const groups = [];
      let currentGroup = null;

      confirmedWalls.forEach((wall, index) => {
        if (index === 0 || !wall.continuesFromPrevious) {
          // Start new group
          currentGroup = { walls: [wall], startLetter: 'A' };
          groups.push(currentGroup);
        } else {
          // Add to current group
          currentGroup.walls.push(wall);
        }
      });

      // Calculate totals for each group
      let totalPanels = 0;
      let totalSqFt = 0;
      let tallestPanelHeight = 0;
      const panelCounts = { A: 0, B: 0, C: 0 };

      groups.forEach(group => {
        // Find tallest wall in group for height adjustment
        let groupMaxHeight = 0;
        group.walls.forEach(wall => {
          const wallHeightInches = utils.feetInchesToInches(wall.heightFt, wall.heightIn);
          const panelHeight = wallHeightInches + config.bleed;
          if (panelHeight > groupMaxHeight) groupMaxHeight = panelHeight;
          if (panelHeight > tallestPanelHeight) tallestPanelHeight = panelHeight;
        });

        // Calculate total width and panels for group
        let groupTotalWidth = 0;
        group.walls.forEach(wall => {
          const wallWidthInches = utils.feetInchesToInches(wall.widthFt, wall.widthIn);
          groupTotalWidth += wallWidthInches;
        });

        const groupPanels = utils.ceil(groupTotalWidth / config.panelWidth);
        const groupSqFtPerPanel = utils.ceil((config.panelWidth * groupMaxHeight) / 144);
        const groupSqFt = groupPanels * groupSqFtPerPanel;

        totalPanels += groupPanels;
        totalSqFt += groupSqFt;

        // Generate sequence for group
        const sequence = this.generateSequence(groupPanels, group.startLetter);
        sequence.forEach(letter => {
          panelCounts[letter]++;
        });

        // Assign panels to individual walls in group
        let coverageUsed = 0;
        let sequenceIndex = 0;

        group.walls.forEach(wall => {
          const wallWidthInches = utils.feetInchesToInches(wall.widthFt, wall.widthIn);
          const wallStart = coverageUsed;
          coverageUsed += wallWidthInches;

          const startPanel = Math.floor(wallStart / config.panelWidth);
          const endPanel = utils.ceil(coverageUsed / config.panelWidth);
          const wallPanels = endPanel - startPanel;

          wall.calculatedPanels = wallPanels;
          wall.calculatedPanelHeight = groupMaxHeight;
          wall.calculatedSqFt = wallPanels * groupSqFtPerPanel;
          wall.sequence = sequence.slice(startPanel, endPanel);
          wall.groupId = groups.indexOf(group) + 1;
        });
      });

      // Calculate breakdown string
      const breakdown = config.repeat === 'ABC'
        ? `(${panelCounts.A})A (${panelCounts.B})B (${panelCounts.C})C`
        : config.repeat === 'A'
          ? `(${panelCounts.A})A`
          : `(${panelCounts.A})A (${panelCounts.B})B`;

      return {
        totalPanels,
        totalSqFt,
        tallestPanelHeight,
        panelCounts,
        breakdown,
        groups
      };
    },

    calculateOverage(totals) {
      if (!totals || state.overage.type === 'none') {
        return { panels: 0, sqFt: 0, breakdown: 'None' };
      }

      let ovA = 0, ovB = 0, ovC = 0;

      if (state.overage.type === 'standard') {
        ovA = 1;
        ovB = config.repeat === 'A' ? 0 : 1;
        ovC = config.repeat === 'ABC' ? 1 : 0;
      } else if (state.overage.type === 'custom') {
        ovA = utils.num(state.overage.customPanels.A);
        ovB = config.repeat === 'A' ? 0 : utils.num(state.overage.customPanels.B);
        ovC = config.repeat === 'ABC' ? utils.num(state.overage.customPanels.C) : 0;
      }

      const totalPanels = ovA + ovB + ovC;
      const sqFtPerPanel = utils.ceil((config.panelWidth * totals.tallestPanelHeight) / 144);
      const totalSqFt = totalPanels * sqFtPerPanel;

      const breakdown = totalPanels > 0
        ? (config.repeat === 'ABC'
          ? `(${ovA})A (${ovB})B (${ovC})C`
          : config.repeat === 'A'
            ? `(${ovA})A`
            : `(${ovA})A (${ovB})B`)
        : 'None';

      return {
        panels: totalPanels,
        sqFt: totalSqFt,
        breakdown,
        counts: { A: ovA, B: ovB, C: ovC }
      };
    }
  };

  // ============================================
  // Yardage Calculation Engine
  // For commercial yardage products (non-panel)
  // ============================================
  const yardageCalculator = {
    /**
     * Calculate number of strips needed to cover wall width
     */
    calculateStripsForWall(widthInches) {
      if (widthInches <= 0) return 0;
      return Math.max(utils.ceil(widthInches / config.yardageWidth), 1);
    },

    /**
     * Calculate effective height after adding buffer and rounding to repeat
     * Formula: CEIL((wall_height + 6") / repeat_height) x repeat_height
     */
    calculateEffectiveHeight(heightInches) {
      const heightWithBuffer = heightInches + CONFIG.YARDAGE_INSTALLATION_BUFFER;
      const repeatsNeeded = utils.ceil(heightWithBuffer / config.repeatHeight);
      return Math.round(repeatsNeeded * config.repeatHeight * 100) / 100;
    },

    /**
     * Calculate yards per strip based on effective height
     */
    calculateYardsPerStrip(effectiveHeightInches) {
      return effectiveHeightInches / 36; // 36 inches per yard
    },

    /**
     * Calculate square footage for reference
     */
    calculateSqFt(widthInches, heightInches) {
      return Math.round((widthInches * heightInches) / 144 * 100) / 100;
    },

    /**
     * Calculate full details for a single wall
     */
    calculateWall(widthInches, heightInches) {
      if (widthInches <= 0 || heightInches <= 0) return null;

      const strips = this.calculateStripsForWall(widthInches);
      const effectiveHeightInches = this.calculateEffectiveHeight(heightInches);
      const yardsPerStrip = this.calculateYardsPerStrip(effectiveHeightInches);
      const totalYards = strips * yardsPerStrip;
      const sqFt = this.calculateSqFt(widthInches, heightInches);

      return {
        strips,
        effectiveHeightInches,
        yardsPerStrip: utils.ceil(yardsPerStrip),
        totalYards: utils.ceil(totalYards),
        sqFt
      };
    },

    /**
     * Calculate totals across all confirmed walls
     * Groups walls by continuesFromPrevious flag (same as panel calculator)
     */
    calculateTotals() {
      const confirmedWalls = state.walls.filter(w => w.confirmed);
      if (confirmedWalls.length === 0) return null;

      // Group walls by connection
      const groups = [];
      let currentGroup = null;

      confirmedWalls.forEach((wall, index) => {
        if (index === 0 || !wall.continuesFromPrevious) {
          // Start new group
          currentGroup = { walls: [wall] };
          groups.push(currentGroup);
        } else {
          // Add to current group
          currentGroup.walls.push(wall);
        }
      });

      // Calculate totals for each group
      let totalStrips = 0;
      let totalYards = 0;
      let totalSqFt = 0;

      groups.forEach((group, groupIndex) => {
        // Calculate total width of the group (sum of all connected walls)
        let groupTotalWidthInches = 0;
        let tallestHeightInches = 0;

        group.walls.forEach(wall => {
          const wallWidthInches = utils.feetInchesToInches(wall.widthFt, wall.widthIn);
          const wallHeightInches = utils.feetInchesToInches(wall.heightFt, wall.heightIn);
          groupTotalWidthInches += wallWidthInches;
          if (wallHeightInches > tallestHeightInches) {
            tallestHeightInches = wallHeightInches;
          }
        });

        // Calculate strips for entire group width
        const groupStrips = groupTotalWidthInches > 0
          ? Math.max(utils.ceil(groupTotalWidthInches / config.yardageWidth), 1)
          : 0;

        // Calculate effective height for group (based on tallest wall)
        const groupEffectiveHeight = this.calculateEffectiveHeight(tallestHeightInches);

        // Calculate yards for the group
        const yardsPerStrip = this.calculateYardsPerStrip(groupEffectiveHeight);
        const groupYards = groupStrips * yardsPerStrip;

        // Calculate group square footage
        let groupSqFt = 0;
        group.walls.forEach(wall => {
          const wallWidthInches = utils.feetInchesToInches(wall.widthFt, wall.widthIn);
          const wallHeightInches = utils.feetInchesToInches(wall.heightFt, wall.heightIn);
          groupSqFt += this.calculateSqFt(wallWidthInches, wallHeightInches);
        });

        // Distribute calculation results to individual walls in group
        let coverageUsed = 0;
        group.walls.forEach(wall => {
          const wallWidthInches = utils.feetInchesToInches(wall.widthFt, wall.widthIn);
          const wallHeightInches = utils.feetInchesToInches(wall.heightFt, wall.heightIn);
          const wallStart = coverageUsed;
          coverageUsed += wallWidthInches;

          // Calculate strips this wall "touches"
          const startStrip = Math.floor(wallStart / config.yardageWidth);
          const endStrip = wallWidthInches > 0
            ? utils.ceil(coverageUsed / config.yardageWidth) - 1
            : startStrip;
          const wallStrips = endStrip - startStrip + 1;

          wall.calculatedStrips = wallStrips;
          wall.calculatedEffectiveHeight = groupEffectiveHeight;
          wall.calculatedYardsPerStrip = utils.ceil(yardsPerStrip);
          wall.calculatedYards = utils.ceil(wallStrips * yardsPerStrip);
          wall.calculatedSqFt = this.calculateSqFt(wallWidthInches, wallHeightInches);
          wall.groupId = groupIndex + 1;
        });

        totalStrips += groupStrips;
        totalYards += groupYards;
        totalSqFt += groupSqFt;
      });

      // Calculate bolt breakdown
      const bolts = this.calculateBoltBreakdown(totalYards);

      return {
        totalStrips,
        totalYards: utils.ceil(totalYards),
        totalSqFt: Math.round(totalSqFt * 100) / 100,
        bolts,
        groups
      };
    },

    /**
     * Calculate bolt breakdown for a given total yardage
     * Maximum 45 yards per bolt
     */
    calculateBoltBreakdown(totalYards) {
      const bolts = [];
      let remaining = totalYards;
      let boltNumber = 1;

      while (remaining > 0) {
        if (remaining > CONFIG.YARDAGE_MAX_YARDS_PER_BOLT) {
          bolts.push({ boltNumber, yards: CONFIG.YARDAGE_MAX_YARDS_PER_BOLT });
          remaining -= CONFIG.YARDAGE_MAX_YARDS_PER_BOLT;
        } else {
          bolts.push({ boltNumber, yards: utils.ceil(remaining) });
          remaining = 0;
        }
        boltNumber++;
      }

      return bolts;
    },

    /**
     * Calculate overage for yardage
     */
    calculateOverage(totals) {
      if (!totals || state.overage.type === 'none') {
        return {
          type: 'none',
          additionalYards: 0,
          totalYards: totals ? totals.totalYards : 0,
          totalSqFt: totals ? totals.totalSqFt : 0
        };
      }

      let additionalYards = 0;

      if (state.overage.type === 'standard') {
        // 15% overage, rounded up
        additionalYards = utils.ceil(totals.totalYards * 0.15);
      } else if (state.overage.type === 'custom') {
        additionalYards = utils.num(state.overage.customYards);
      }

      const totalYards = totals.totalYards + additionalYards;
      // Calculate proportional sq ft increase
      const sqFtRatio = totals.totalYards > 0 ? totalYards / totals.totalYards : 1;
      const totalSqFt = Math.round(totals.totalSqFt * sqFtRatio * 100) / 100;

      return {
        type: state.overage.type,
        additionalYards,
        totalYards: utils.ceil(totalYards),
        totalSqFt
      };
    },

    /**
     * Validate minimum yardage requirement (10 yards)
     */
    validateMinimum(totalYards) {
      if (totalYards < CONFIG.YARDAGE_MIN_ORDER_YARDS) {
        return {
          valid: false,
          message: `Minimum order is ${CONFIG.YARDAGE_MIN_ORDER_YARDS} yards. Current total: ${totalYards} yards.`
        };
      }
      return { valid: true };
    }
  };

  // ============================================
  // Wall Management
  // ============================================
  const wallManager = {
    addWall(prefill = {}) {
      if (state.walls.length >= CONFIG.MAX_WALLS) return;

      state.wallCounter++;
      const wallId = `wall-${state.wallCounter}`;
      const wallNumber = state.walls.length + 1;

      const wall = {
        id: wallId,
        number: wallNumber,
        heightFt: prefill.heightFt || 0,
        heightIn: prefill.heightIn || 0,
        widthFt: prefill.widthFt || 0,
        widthIn: prefill.widthIn || 0,
        confirmed: false,
        continuesFromPrevious: wallNumber > 1,
        calculatedPanels: 0,
        calculatedPanelHeight: 0,
        calculatedSqFt: 0,
        sequence: [],
        groupId: 1
      };

      state.walls.push(wall);
      this.renderWall(wall);
      this.updateAddWallButton();
    },

    renderWall(wall) {
      const template = dom.wallTemplate.content.cloneNode(true);
      const wallEl = template.querySelector('[data-mc-wall]');

      wallEl.dataset.wallId = wall.id;
      wallEl.dataset.wallNumber = wall.number;

      // Update wall label
      const label = wallEl.querySelector('[data-wall-label]');
      if (label) label.textContent = `Wall ${wall.number}`;

      // Hide help icon for walls after first
      if (wall.number > 1) {
        const helpIcon = wallEl.querySelector('[data-mc-help-trigger="instructions"]');
        if (helpIcon) helpIcon.style.display = 'none';
      }

      // Show/hide connection toggle
      const connectionEl = wallEl.querySelector('[data-wall-connection]');
      if (wall.number === 1 && connectionEl) {
        connectionEl.style.display = 'none';
      }

      // Show remove button for walls after first
      const removeBtn = wallEl.querySelector('[data-wall-remove]');
      if (wall.number === 1 && removeBtn) {
        removeBtn.style.display = 'none';
      }

      // Update connection label text for walls > 2
      if (wall.number > 1) {
        const checkboxLabel = wallEl.querySelector('.mc-checkbox__label');
        if (checkboxLabel) {
          // Get just the text content before any help icon
          const helpIcon = checkboxLabel.querySelector('.mc-help-icon');
          if (helpIcon) {
            checkboxLabel.innerHTML = `Continues from Wall ${wall.number - 1} `;
            checkboxLabel.appendChild(helpIcon);
          }
        }
      }

      // Setup event listeners
      this.setupWallListeners(wallEl, wall);

      dom.wallsContainer.appendChild(wallEl);
    },

    setupWallListeners(wallEl, wall) {
      const heightFtSelect = wallEl.querySelector('[data-wall-height-ft]');
      const heightInSelect = wallEl.querySelector('[data-wall-height-in]');
      const widthFtSelect = wallEl.querySelector('[data-wall-width-ft]');
      const widthInSelect = wallEl.querySelector('[data-wall-width-in]');
      const confirmBtn = wallEl.querySelector('[data-wall-confirm]');
      const editBtn = wallEl.querySelector('[data-wall-edit]');
      const saveBtn = wallEl.querySelector('[data-wall-save]');
      const cancelBtn = wallEl.querySelector('[data-wall-cancel]');
      const removeBtn = wallEl.querySelector('[data-wall-remove]');
      const continuesCheckbox = wallEl.querySelector('[data-wall-continues]');

      // Store original values for cancel
      let originalValues = { heightFt: 0, heightIn: 0, widthFt: 0, widthIn: 0 };

      // Input handlers for feet/inches dropdowns
      const handleInput = () => {
        wall.heightFt = utils.num(heightFtSelect.value);
        wall.heightIn = utils.num(heightInSelect.value);
        wall.widthFt = utils.num(widthFtSelect.value);
        wall.widthIn = utils.num(widthInSelect.value);
        this.validateWall(wallEl, wall);
        this.updateWallPreview(wallEl, wall);
      };

      heightFtSelect.addEventListener('change', handleInput);
      heightInSelect.addEventListener('change', handleInput);
      widthFtSelect.addEventListener('change', handleInput);
      widthInSelect.addEventListener('change', handleInput);

      // Connection toggle
      if (continuesCheckbox) {
        continuesCheckbox.addEventListener('change', () => {
          wall.continuesFromPrevious = continuesCheckbox.checked;
          const connectionEl = wallEl.querySelector('[data-wall-connection]');
          const noteEl = wallEl.querySelector('[data-connection-note]');

          if (wall.continuesFromPrevious) {
            utils.removeClass(connectionEl, 'disconnected');
            if (noteEl) noteEl.textContent = 'Pattern continues from previous wall';
          } else {
            utils.addClass(connectionEl, 'disconnected');
            if (noteEl) noteEl.textContent = 'Starts fresh with Panel A';
          }

          if (wall.confirmed) {
            render();
          }
        });
      }

      // Confirm button
      confirmBtn.addEventListener('click', () => {
        if (!this.validateWall(wallEl, wall)) return;

        wall.confirmed = true;
        state.confirmedWalls.add(wall.id);

        utils.addClass(wallEl, 'mc-wall--confirmed');
        heightFtSelect.disabled = true;
        heightInSelect.disabled = true;
        widthFtSelect.disabled = true;
        widthInSelect.disabled = true;
        if (continuesCheckbox) continuesCheckbox.disabled = true;

        utils.hide(confirmBtn);
        utils.show(editBtn);

        const statusEl = wallEl.querySelector('[data-wall-status]');
        if (statusEl) {
          statusEl.innerHTML = '<span class="mc-wall__status-badge mc-wall__status-badge--confirmed">CONFIRMED</span>';
        }

        this.updateAddWallButton();
        render();
      });

      // Edit button
      editBtn.addEventListener('click', () => {
        // Store current values before editing
        originalValues = {
          heightFt: wall.heightFt,
          heightIn: wall.heightIn,
          widthFt: wall.widthFt,
          widthIn: wall.widthIn
        };

        heightFtSelect.disabled = false;
        heightInSelect.disabled = false;
        widthFtSelect.disabled = false;
        widthInSelect.disabled = false;
        if (continuesCheckbox) continuesCheckbox.disabled = false;

        utils.addClass(wallEl, 'mc-wall--editing');
        utils.hide(editBtn);
        utils.show(saveBtn);
        utils.show(cancelBtn);

        const statusEl = wallEl.querySelector('[data-wall-status]');
        if (statusEl) {
          statusEl.innerHTML = '<span class="mc-wall__status-badge mc-wall__status-badge--editing">EDITING</span>';
        }

        heightFtSelect.focus();
      });

      // Save button
      saveBtn.addEventListener('click', () => {
        if (!this.validateWall(wallEl, wall)) return;

        heightFtSelect.disabled = true;
        heightInSelect.disabled = true;
        widthFtSelect.disabled = true;
        widthInSelect.disabled = true;
        if (continuesCheckbox) continuesCheckbox.disabled = true;

        utils.removeClass(wallEl, 'mc-wall--editing');
        utils.show(editBtn);
        utils.hide(saveBtn);
        utils.hide(cancelBtn);

        const statusEl = wallEl.querySelector('[data-wall-status]');
        if (statusEl) {
          statusEl.innerHTML = '<span class="mc-wall__status-badge mc-wall__status-badge--confirmed">CONFIRMED</span>';
        }

        render();
      });

      // Cancel button
      if (cancelBtn) {
        cancelBtn.addEventListener('click', () => {
          // Restore original values
          wall.heightFt = originalValues.heightFt;
          wall.heightIn = originalValues.heightIn;
          wall.widthFt = originalValues.widthFt;
          wall.widthIn = originalValues.widthIn;

          heightFtSelect.value = String(originalValues.heightFt);
          heightInSelect.value = String(originalValues.heightIn);
          widthFtSelect.value = String(originalValues.widthFt);
          widthInSelect.value = String(originalValues.widthIn);

          heightFtSelect.disabled = true;
          heightInSelect.disabled = true;
          widthFtSelect.disabled = true;
          widthInSelect.disabled = true;
          if (continuesCheckbox) continuesCheckbox.disabled = true;

          utils.removeClass(wallEl, 'mc-wall--editing');
          utils.show(editBtn);
          utils.hide(saveBtn);
          utils.hide(cancelBtn);

          const statusEl = wallEl.querySelector('[data-wall-status]');
          if (statusEl) {
            statusEl.innerHTML = '<span class="mc-wall__status-badge mc-wall__status-badge--confirmed">CONFIRMED</span>';
          }

          this.updateWallPreview(wallEl, wall);
        });
      }

      // Remove button
      removeBtn.addEventListener('click', () => {
        this.removeWall(wall);
      });
    },

    validateWall(wallEl, wall) {
      const validationEl = wallEl.querySelector('[data-wall-validation]');
      const confirmBtn = wallEl.querySelector('[data-wall-confirm]');
      const saveBtn = wallEl.querySelector('[data-wall-save]');

      const heightInches = utils.feetInchesToInches(wall.heightFt, wall.heightIn);
      const widthInches = utils.feetInchesToInches(wall.widthFt, wall.widthIn);
      const errors = [];
      const infos = [];

      // Reset
      utils.hide(validationEl);

      if (heightInches === 0 || widthInches === 0) {
        if (confirmBtn) confirmBtn.disabled = true;
        if (saveBtn) saveBtn.disabled = true;
        return false;
      }

      if (config.mode === 'yardage') {
        // Yardage mode validation
        // Check minimum width - soft info message
        if (widthInches < config.yardageWidth) {
          infos.push(`Wall width is less than one full drop (${config.yardageWidth}"). You'll receive 1 full-width drop for this wall.`);
        }

        // Yardage has no max height constraint like panels
        // Minimum height still applies for practical reasons
        if (heightInches < CONFIG.MIN_PANEL_HEIGHT) {
          errors.push(`Height must be at least ${CONFIG.MIN_PANEL_HEIGHT}" (minimum production height)`);
        }
      } else {
        // Panel mode validation
        // Check minimum width - soft info message, not a blocking error
        if (widthInches < config.panelWidth) {
          infos.push(`Wall width is less than one full panel (${config.panelWidth}"). You'll receive 1 full-width panel for this wall.`);
        }

        // Check minimum height - blocking error
        if (heightInches < CONFIG.MIN_PANEL_HEIGHT) {
          errors.push(`Height must be at least ${CONFIG.MIN_PANEL_HEIGHT}" (minimum production height)`);
        }

        // Check maximum height - blocking error (panel mode only)
        const panelHeight = heightInches + config.bleed;
        if (panelHeight > config.maxHeight) {
          errors.push(`Panel height ${panelHeight}" exceeds maximum of ${config.maxHeight}". Please contact us for a custom quote.`);
        }
      }

      // Show blocking errors
      if (errors.length > 0) {
        if (validationEl) {
          utils.renderMessages(validationEl, errors);
          utils.addClass(validationEl, 'error');
          utils.removeClass(validationEl, 'info');
          utils.show(validationEl);
        }
        if (confirmBtn) confirmBtn.disabled = true;
        if (saveBtn) saveBtn.disabled = true;
        return false;
      }

      // Show info messages (non-blocking)
      if (infos.length > 0) {
        if (validationEl) {
          utils.renderMessages(validationEl, infos);
          utils.removeClass(validationEl, 'error');
          utils.addClass(validationEl, 'info');
          utils.show(validationEl);
        }
      }

      if (confirmBtn) confirmBtn.disabled = false;
      if (saveBtn) saveBtn.disabled = false;
      return true;
    },

    updateWallPreview(wallEl, wall) {
      const previewEl = wallEl.querySelector('[data-wall-preview]');
      const heightInches = utils.feetInchesToInches(wall.heightFt, wall.heightIn);
      const widthInches = utils.feetInchesToInches(wall.widthFt, wall.widthIn);

      if (config.mode === 'yardage') {
        // Yardage mode preview
        const calc = yardageCalculator.calculateWall(widthInches, heightInches);

        if (!calc) {
          utils.hide(previewEl);
          return;
        }

        // For yardage, we show strips, effective height, and yards
        const stripsEl = wallEl.querySelector('[data-wall-strips]');
        const effectiveHeightEl = wallEl.querySelector('[data-wall-effective-height]');
        const yardsEl = wallEl.querySelector('[data-wall-yards]');

        // Fallback to panel elements if yardage-specific elements don't exist
        const panelsEl = wallEl.querySelector('[data-wall-panels]');
        const panelHeightEl = wallEl.querySelector('[data-wall-panel-height]');
        const sqFtEl = wallEl.querySelector('[data-wall-sqft-panel]');

        if (stripsEl) stripsEl.textContent = calc.strips;
        else if (panelsEl) panelsEl.textContent = calc.strips;

        if (effectiveHeightEl) effectiveHeightEl.textContent = calc.effectiveHeightInches;
        else if (panelHeightEl) panelHeightEl.textContent = calc.effectiveHeightInches;

        if (yardsEl) yardsEl.textContent = calc.totalYards;
        else if (sqFtEl) sqFtEl.textContent = calc.totalYards;

        utils.show(previewEl);
      } else {
        // Panel mode preview (original logic)
        const calc = calculator.calculateWall(widthInches, heightInches);

        if (!calc) {
          utils.hide(previewEl);
          return;
        }

        const panelsEl = wallEl.querySelector('[data-wall-panels]');
        const panelHeightEl = wallEl.querySelector('[data-wall-panel-height]');
        const sqFtEl = wallEl.querySelector('[data-wall-sqft-panel]');

        if (panelsEl) panelsEl.textContent = calc.panels;
        if (panelHeightEl) panelHeightEl.textContent = calc.panelHeight;
        if (sqFtEl) sqFtEl.textContent = calc.sqFtPerPanel;

        utils.show(previewEl);
      }
    },

    removeWall(wall) {
      const index = state.walls.indexOf(wall);
      if (index === -1 || wall.number === 1) return;

      state.walls.splice(index, 1);
      state.confirmedWalls.delete(wall.id);

      // Remove DOM element
      const wallEl = dom.wallsContainer.querySelector(`[data-wall-id="${wall.id}"]`);
      if (wallEl) wallEl.remove();

      // Renumber remaining walls
      state.walls.forEach((w, i) => {
        w.number = i + 1;
        const el = dom.wallsContainer.querySelector(`[data-wall-id="${w.id}"]`);
        if (el) {
          el.dataset.wallNumber = w.number;
          const label = el.querySelector('[data-wall-label]');
          if (label) label.textContent = `Wall ${w.number}`;
        }
      });

      this.updateAddWallButton();
      render();
    },

    updateAddWallButton() {
      const hasConfirmed = state.confirmedWalls.size > 0;
      const canAddMore = state.walls.length < CONFIG.MAX_WALLS;

      if (hasConfirmed && canAddMore) {
        utils.show(dom.addWallBtn);
      } else {
        utils.hide(dom.addWallBtn);
      }
    }
  };

  // ============================================
  // UI Rendering
  // ============================================
  function render() {
    // Use appropriate calculator based on mode
    const totals = config.mode === 'yardage'
      ? yardageCalculator.calculateTotals()
      : calculator.calculateTotals();
    state.totals = totals;

    if (!totals) {
      utils.hide(dom.summarySection);
      utils.hide(dom.overageSection);
      utils.hide(dom.cfaSection);
      utils.hide(dom.projectSection);
      utils.hide(dom.submitSection);
      return;
    }

    // Show all sections
    utils.show(dom.summarySection);
    utils.show(dom.overageSection);
    utils.show(dom.cfaSection);
    utils.show(dom.projectSection);
    utils.show(dom.submitSection);

    // Update summary based on mode
    if (config.mode === 'yardage') {
      renderYardageSummary(totals);
    } else {
      renderSummary(totals);
    }

    // Update overage based on mode
    if (config.mode === 'yardage') {
      renderYardageOverage(totals);
    } else {
      renderOverage(totals);
    }

    // Update submit button state
    updateSubmitButton();
  }

  function renderSummary(totals) {
    // Wall groups
    const groupsEl = root.querySelector('[data-mc-wall-groups]');
    const groupsDisplay = root.querySelector('[data-mc-groups-display]');

    if (totals.groups.length > 1) {
      const groupTexts = totals.groups.map((g, i) => {
        const wallNums = g.walls.map(w => w.number).join(', ');
        return `G${i + 1}: Walls ${wallNums}`;
      });
      if (groupsDisplay) groupsDisplay.textContent = groupTexts.join(' | ');
      utils.show(groupsEl);
    } else {
      utils.hide(groupsEl);
    }

    // Wall details
    const wallsSummary = root.querySelector('[data-mc-walls-summary]');
    if (wallsSummary) {
      const confirmedWalls = state.walls.filter(w => w.confirmed);
      // Clear existing content safely
      wallsSummary.innerHTML = '';
      confirmedWalls.forEach(wall => {
        const heightInches = utils.feetInchesToInches(wall.heightFt, wall.heightIn);
        const widthInches = utils.feetInchesToInches(wall.widthFt, wall.widthIn);
        const heightStr = utils.formatFeetInches(heightInches);
        const widthStr = utils.formatFeetInches(widthInches);

        const row = document.createElement('div');
        row.className = 'mc-summary__row';

        const label = document.createElement('span');
        label.className = 'mc-summary__label';
        label.textContent = `Wall ${wall.number}${totals.groups.length > 1 ? ` (G${wall.groupId})` : ''}`;

        const value = document.createElement('span');
        value.className = 'mc-summary__value';
        value.textContent = `${heightStr}h × ${widthStr}w → ${wall.calculatedPanels} panels`;

        row.appendChild(label);
        row.appendChild(value);
        wallsSummary.appendChild(row);
      });
    }

    // Totals
    const totalPanelsEl = root.querySelector('[data-mc-total-panels]');
    const breakdownEl = root.querySelector('[data-mc-panel-breakdown]');
    const totalSqFtEl = root.querySelector('[data-mc-total-sqft]');
    const projectTotalsEl = root.querySelector('[data-mc-project-totals]');
    const projectBreakdownEl = root.querySelector('[data-mc-project-breakdown]');

    if (totalPanelsEl) totalPanelsEl.textContent = totals.totalPanels;
    if (breakdownEl) breakdownEl.textContent = totals.breakdown;
    if (totalSqFtEl) totalSqFtEl.textContent = `${totals.totalSqFt} sq ft`;

    if (projectTotalsEl && projectBreakdownEl) {
      projectBreakdownEl.textContent = `${totals.totalPanels} Total ${totals.breakdown}`;
      utils.show(projectTotalsEl);
    }

    // Save state after render
    storage.save();
  }

  function renderOverage(totals) {
    const overage = calculator.calculateOverage(totals);
    const summaryEl = root.querySelector('[data-mc-overage-summary]');
    const panelsEl = root.querySelector('[data-mc-overage-panels]');
    const sqFtEl = root.querySelector('[data-mc-overage-sqft]');

    if (overage.panels > 0) {
      if (panelsEl) panelsEl.textContent = `${overage.panels} Total ${overage.breakdown}`;
      if (sqFtEl) sqFtEl.textContent = `${overage.sqFt} sq ft`;
      utils.show(summaryEl);
    } else {
      utils.hide(summaryEl);
    }
  }

  // ============================================
  // Yardage-specific Rendering
  // ============================================
  function renderYardageSummary(totals) {
    // Wall groups (same logic as panel mode)
    const groupsEl = root.querySelector('[data-mc-wall-groups]');
    const groupsDisplay = root.querySelector('[data-mc-groups-display]');

    if (totals.groups && totals.groups.length > 1) {
      const groupTexts = totals.groups.map((g, i) => {
        const wallNums = g.walls.map(w => w.number).join(', ');
        return `G${i + 1}: Walls ${wallNums}`;
      });
      if (groupsDisplay) groupsDisplay.textContent = groupTexts.join(' | ');
      utils.show(groupsEl);
    } else {
      utils.hide(groupsEl);
    }

    // Wall details - yardage mode shows strips and yards
    const wallsSummary = root.querySelector('[data-mc-walls-summary]');
    if (wallsSummary) {
      const confirmedWalls = state.walls.filter(w => w.confirmed);
      wallsSummary.innerHTML = '';
      confirmedWalls.forEach(wall => {
        const heightInches = utils.feetInchesToInches(wall.heightFt, wall.heightIn);
        const widthInches = utils.feetInchesToInches(wall.widthFt, wall.widthIn);
        const heightStr = utils.formatFeetInches(heightInches);
        const widthStr = utils.formatFeetInches(widthInches);

        const row = document.createElement('div');
        row.className = 'mc-summary__row';

        const label = document.createElement('span');
        label.className = 'mc-summary__label';
        label.textContent = `Wall ${wall.number}${totals.groups && totals.groups.length > 1 ? ` (G${wall.groupId})` : ''}`;

        const value = document.createElement('span');
        value.className = 'mc-summary__value';
        value.textContent = `${heightStr}h x ${widthStr}w - ${wall.calculatedStrips || 0} drops, ${wall.calculatedYards || 0} yds`;

        row.appendChild(label);
        row.appendChild(value);
        wallsSummary.appendChild(row);
      });
    }

    // Totals - yardage mode
    const totalPanelsEl = root.querySelector('[data-mc-total-panels]');
    const breakdownEl = root.querySelector('[data-mc-panel-breakdown]');
    const totalSqFtEl = root.querySelector('[data-mc-total-sqft]');
    const projectTotalsEl = root.querySelector('[data-mc-project-totals]');
    const projectBreakdownEl = root.querySelector('[data-mc-project-breakdown]');

    // Yardage-specific elements (if they exist)
    const totalYardsEl = root.querySelector('[data-mc-total-yards]');
    const totalStripsEl = root.querySelector('[data-mc-total-strips]');
    const boltBreakdownEl = root.querySelector('[data-mc-bolt-breakdown]');

    // Update yardage-specific elements if they exist
    if (totalYardsEl) totalYardsEl.textContent = totals.totalYards;
    if (totalStripsEl) totalStripsEl.textContent = totals.totalStrips;

    // Bolt breakdown
    if (boltBreakdownEl && totals.bolts) {
      boltBreakdownEl.innerHTML = '';
      totals.bolts.forEach(bolt => {
        const boltSpan = document.createElement('span');
        boltSpan.className = 'mc-bolt';
        boltSpan.textContent = `Bolt ${bolt.boltNumber}: ${bolt.yards} yds`;
        boltBreakdownEl.appendChild(boltSpan);
      });
    }

    // Fallback to panel elements for backward compatibility
    if (totalPanelsEl) totalPanelsEl.textContent = totals.totalStrips;
    if (breakdownEl) {
      // Show bolt info in breakdown for yardage
      const boltInfo = totals.bolts.length === 1
        ? `1 bolt`
        : `${totals.bolts.length} bolts`;
      breakdownEl.textContent = `${totals.totalYards} yds (${boltInfo})`;
    }
    if (totalSqFtEl) totalSqFtEl.textContent = `${totals.totalSqFt} sq ft`;

    if (projectTotalsEl && projectBreakdownEl) {
      projectBreakdownEl.textContent = `${totals.totalStrips} Drops, ${totals.totalYards} Yards`;
      utils.show(projectTotalsEl);
    }

    // Save state after render
    storage.save();
  }

  function renderYardageOverage(totals) {
    const overage = yardageCalculator.calculateOverage(totals);
    const summaryEl = root.querySelector('[data-mc-overage-summary]');
    const panelsEl = root.querySelector('[data-mc-overage-panels]');
    const sqFtEl = root.querySelector('[data-mc-overage-sqft]');

    // Yardage-specific elements
    const yardsEl = root.querySelector('[data-mc-overage-yards]');

    if (overage.additionalYards > 0) {
      // Yardage-specific display
      if (yardsEl) {
        yardsEl.textContent = `+${overage.additionalYards} yards`;
      }

      // Fallback to panel elements
      if (panelsEl) {
        const overagePercent = state.overage.type === 'standard' ? '(15%)' : '';
        panelsEl.textContent = `+${overage.additionalYards} yards ${overagePercent}`;
      }
      if (sqFtEl) sqFtEl.textContent = `Total: ${overage.totalYards} yds / ${overage.totalSqFt} sq ft`;

      utils.show(summaryEl);
    } else {
      utils.hide(summaryEl);
    }

    // Update bolt breakdown to reflect overage
    updateBoltBreakdownWithOverage(totals, overage);
  }

  function updateBoltBreakdownWithOverage(totals, overage) {
    const boltBreakdownEl = root.querySelector('[data-mc-bolt-breakdown]');
    const boltListEl = root.querySelector('[data-mc-bolt-list]');
    if (!boltBreakdownEl || !totals) return;

    const hasOverage = overage && overage.additionalYards > 0;
    const finalYards = hasOverage ? overage.totalYards : totals.totalYards;
    const displayBolts = yardageCalculator.calculateBoltBreakdown(finalYards);

    // Re-render bolt list
    if (boltListEl) {
      boltListEl.innerHTML = '';
      displayBolts.forEach((bolt, idx) => {
        const boltSpan = document.createElement('span');
        boltSpan.className = 'mc-bolt';
        let label = `Bolt ${bolt.boltNumber}: ${bolt.yards} yds`;
        if (hasOverage && idx === displayBolts.length - 1) {
          label += ` (includes ${overage.additionalYards} yds overage)`;
        }
        boltSpan.textContent = label;
        boltListEl.appendChild(boltSpan);
      });
    }

    // Show/hide bolt section
    if (displayBolts.length > 1 || hasOverage) {
      utils.show(boltBreakdownEl);
    }
  }

  function updateSubmitButton() {
    if (!dom.submitBtn) return;

    const canSubmit = state.totals &&
                      state.confirmedWalls.size > 0 &&
                      state.cfa.type !== null &&
                      !state.isSubmitting;

    dom.submitBtn.disabled = !canSubmit;
  }

  // ============================================
  // Material Helpers
  // ============================================
  const MATERIAL_NAMES = {
    'CS': 'Type II Vinyl - Sands',
    'CC': 'Type II Vinyl - Canvas',
    'CG': 'Type II Vinyl - Grasscloth',
    'CSP': 'Type II Vinyl - Pearlescent',
    'CP': 'Type II PVC-Free Paper'
  };

  function getMaterialName(code) {
    return MATERIAL_NAMES[code] || code || 'Unknown Material';
  }

  // ============================================
  // Event Handlers
  // ============================================
  function setupEventListeners() {
    // Accordion
    if (dom.accordion) {
      dom.accordion.addEventListener('click', () => {
        const expanded = dom.accordion.getAttribute('aria-expanded') === 'true';
        dom.accordion.setAttribute('aria-expanded', !expanded);
        dom.accordionContent.setAttribute('aria-hidden', expanded);
      });
    }

    // Login prompt dismiss button
    const loginPrompt = root.querySelector('[data-mc-login-prompt]');
    const dismissBtn = root.querySelector('[data-mc-dismiss-login-prompt]');
    if (dismissBtn && loginPrompt) {
      dismissBtn.addEventListener('click', () => {
        loginPrompt.classList.add('mc-login-prompt--hidden');
      });
    }

    // Change material button - shows material selector
    if (dom.changeMaterialBtn) {
      dom.changeMaterialBtn.addEventListener('click', () => {
        // Save state before navigation
        storage.save();
        // Show a simple material selector modal or navigate to first panel variant
        showMaterialSelectorModal();
      });
    }

    // Material option clicks - save state before navigating
    if (dom.materialSelector) {
      dom.materialSelector.addEventListener('click', (e) => {
        const materialOption = e.target.closest('.mc-material-option');
        if (materialOption) {
          // State will be preserved via sessionStorage
          storage.save();
          // Navigation happens via the href on the link
        }
      });
    }

    // Add wall button
    if (dom.addWallBtn) {
      dom.addWallBtn.addEventListener('click', () => {
        wallManager.addWall();
      });
    }

    // Overage options
    const overageRadios = root.querySelectorAll('[data-mc-overage-type]');
    const overageCustom = root.querySelector('[data-mc-overage-custom]');

    overageRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        state.overage.type = radio.value;

        if (radio.value === 'custom') {
          utils.show(overageCustom);
        } else {
          utils.hide(overageCustom);
        }

        render();
      });
    });

    // Custom overage inputs
    ['A', 'B', 'C'].forEach(letter => {
      const input = root.querySelector(`[data-mc-overage-${letter.toLowerCase()}]`);
      if (input) {
        input.addEventListener('input', () => {
          state.overage.customPanels[letter] = utils.num(input.value);
          render();
        });
      }
    });

    // Single-panel repeat (A): hide Panel B/C custom inputs (the overage snippet always renders Panel B)
    if (config.repeat === 'A') {
      ['B', 'C'].forEach(letter => {
        const input = root.querySelector(`[data-mc-overage-${letter.toLowerCase()}]`);
        if (input) utils.hide(input.closest('.mc-field') || input);
      });
    }

    // CFA options
    const cfaRadios = root.querySelectorAll('[data-mc-cfa-type]');
    const cfaEmailSection = root.querySelector('[data-mc-cfa-email-section]');
    const cfaShipping = root.querySelector('[data-mc-cfa-shipping]');
    const cfaWaiverInfo = root.querySelector('[data-mc-cfa-waiver-info]');

    cfaRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        state.cfa.type = radio.value;

        // Show/hide appropriate sections based on CFA type
        if (radio.value === 'photo') {
          utils.show(cfaEmailSection);
          if (cfaShipping) utils.hide(cfaShipping);
          if (cfaWaiverInfo) utils.hide(cfaWaiverInfo);
        } else if (radio.value === 'physical') {
          if (cfaEmailSection) utils.hide(cfaEmailSection);
          utils.show(cfaShipping);
          if (cfaWaiverInfo) utils.hide(cfaWaiverInfo);
        } else if (radio.value === 'waive') {
          if (cfaEmailSection) utils.hide(cfaEmailSection);
          if (cfaShipping) utils.hide(cfaShipping);
          utils.show(cfaWaiverInfo);
        } else {
          if (cfaEmailSection) utils.hide(cfaEmailSection);
          if (cfaShipping) utils.hide(cfaShipping);
          if (cfaWaiverInfo) utils.hide(cfaWaiverInfo);
        }

        updateSubmitButton();
      });
    });

    // ZIP code validation on blur
    const cfaPostalInput = root.querySelector('[data-mc-cfa-postal]');
    if (cfaPostalInput) {
      cfaPostalInput.addEventListener('blur', () => {
        validateZipCode();
      });
    }

    // Email validation on blur
    const cfaEmailInput = root.querySelector('[data-mc-cfa-email]');
    if (cfaEmailInput) {
      cfaEmailInput.addEventListener('blur', () => {
        validateCfaEmail();
      });
    }

    // Project details
    const sidemarkInput = root.querySelector('[data-mc-sidemark]');
    const projectNameInput = root.querySelector('[data-mc-project-name]');
    const previousOrderInput = root.querySelector('[data-mc-previous-order]');
    const notesInput = root.querySelector('[data-mc-notes]');

    if (sidemarkInput) {
      sidemarkInput.addEventListener('input', () => {
        state.projectDetails.sidemark = sidemarkInput.value;
        storage.save();
      });
    }
    if (projectNameInput) {
      projectNameInput.addEventListener('input', () => {
        state.projectDetails.projectName = projectNameInput.value;
        storage.save();
      });
    }
    if (previousOrderInput) {
      previousOrderInput.addEventListener('input', () => {
        state.projectDetails.previousOrder = previousOrderInput.value;
        storage.save();
      });
    }
    if (notesInput) {
      notesInput.addEventListener('input', () => {
        state.projectDetails.notes = notesInput.value;
        storage.save();
      });
    }

    // Submit button
    if (dom.submitBtn) {
      dom.submitBtn.addEventListener('click', handleSubmit);
    }

    // Save state before navigating to login (state persists in sessionStorage)
    root.addEventListener('click', (e) => {
      const link = e.target.closest('a[href*="/account"]');
      if (link && (link.href.includes('login') || link.href.includes('register'))) {
        // Save current form state before navigating to login
        // The return_url query param with #calculator hash handles the redirect
        storage.save();
      }
    });

    // Help triggers - use event delegation on root
    root.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-mc-help-trigger]');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        let helpKey = trigger.dataset.mcHelpTrigger;
        // Show yardage-specific instructions when in yardage mode
        if (helpKey === 'instructions' && config.mode === 'yardage') {
          helpKey = 'yardage-instructions';
        }
        showHelpModal(helpKey);
      }
    });

    // Modal close
    if (dom.modal) {
      dom.modal.addEventListener('click', (e) => {
        if (e.target.matches('[data-mc-modal-close]') || e.target.closest('[data-mc-modal-close]')) {
          closeModal();
        }
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && dom.modal.getAttribute('aria-hidden') === 'false') {
          closeModal();
        }
      });
    }

    // Success modal close
    if (dom.successModal) {
      dom.successModal.addEventListener('click', (e) => {
        if (e.target.matches('[data-mc-success-modal-close]') || e.target.closest('[data-mc-success-modal-close]')) {
          closeSuccessModal();
        }
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && dom.successModal.getAttribute('aria-hidden') === 'false') {
          closeSuccessModal();
        }
      });
    }
  }

  // ============================================
  // Help Modal
  // ============================================
  function showHelpModal(helpKey) {
    // Validate helpKey to prevent injection via data attributes
    const safeHelpKey = String(helpKey).replace(/[^a-zA-Z0-9-_]/g, '');
    const template = document.getElementById(`mc-help-${safeHelpKey}`);
    if (!template || !dom.modal || !dom.modalBody) {
      console.warn('Help modal: template not found for key:', safeHelpKey);
      return;
    }

    // Clear modal body
    dom.modalBody.innerHTML = '';

    if (template.content) {
      // <template> element — import its document fragment
      dom.modalBody.appendChild(document.importNode(template.content, true));
    } else {
      // <script type="text/template"> — parse innerHTML safely via a temporary container
      // Content is trusted (authored in our Liquid templates, not user-supplied)
      const temp = document.createElement('div');
      temp.innerHTML = template.innerHTML;
      while (temp.firstChild) {
        dom.modalBody.appendChild(temp.firstChild);
      }
    }

    // Extract title from first h5
    const h5 = dom.modalBody.querySelector('h5');
    if (h5 && dom.modalTitle) {
      dom.modalTitle.textContent = h5.textContent;
      h5.remove();
    } else if (dom.modalTitle) {
      dom.modalTitle.textContent = 'Help';
    }

    dom.modal.setAttribute('aria-hidden', 'false');
    dom.modal.classList.add('mc-modal--open');
  }

  function closeModal() {
    if (dom.modal) {
      dom.modal.setAttribute('aria-hidden', 'true');
      dom.modal.classList.remove('mc-modal--open');
      // Clear dynamic content when closing
      if (dom.modalBody) {
        dom.modalBody.innerHTML = '';
      }
    }
  }

  function showMaterialSelectorModal() {
    if (!dom.modal || !dom.modalBody) return;

    // Determine which variants to show based on mode
    const variants = config.mode === 'yardage' ? config.yardVariants : config.panelVariants;
    const modalTitle = config.mode === 'yardage' ? 'Select Yardage Material' : 'Select Panel Material';

    // Build material selector content using safe DOM methods
    dom.modalBody.innerHTML = '';

    const selectorDiv = document.createElement('div');
    selectorDiv.className = 'mc-material-selector mc-material-selector--modal';

    variants.forEach(variant => {
      const materialName = getMaterialName(variant.materialCode);
      const isCurrentMaterial = variant.materialCode === config.currentMaterialCode;

      const link = document.createElement('a');
      // Validate URL - only allow relative URLs or same-origin URLs
      const url = String(variant.url || '');
      if (url.startsWith('/') || url.startsWith(window.location.origin)) {
        link.href = url;
      } else {
        link.href = '#'; // Fallback for invalid URLs
      }
      link.className = 'mc-material-option' + (isCurrentMaterial ? ' mc-material-option--selected' : '');
      link.dataset.materialCode = String(variant.materialCode || '');
      link.dataset.variantId = String(variant.id || '');

      // Use event listener instead of inline onclick
      link.addEventListener('click', () => {
        storage.save();
      });

      const nameSpan = document.createElement('span');
      nameSpan.className = 'mc-material-option__name';
      nameSpan.textContent = materialName;
      link.appendChild(nameSpan);

      if (!variant.available) {
        const outOfStockBadge = document.createElement('span');
        outOfStockBadge.className = 'mc-material-option__badge';
        outOfStockBadge.textContent = 'Out of Stock';
        link.appendChild(outOfStockBadge);
      }

      if (isCurrentMaterial) {
        const currentBadge = document.createElement('span');
        currentBadge.className = 'mc-material-option__badge mc-material-option__badge--current';
        currentBadge.textContent = 'Current';
        link.appendChild(currentBadge);
      }

      selectorDiv.appendChild(link);
    });

    dom.modalBody.appendChild(selectorDiv);

    const note = document.createElement('p');
    note.className = 'mc-section__note';
    note.style.marginTop = '16px';
    note.textContent = 'Selecting a material will reload the page. Your calculator data will be preserved.';
    dom.modalBody.appendChild(note);

    if (dom.modalTitle) {
      dom.modalTitle.textContent = modalTitle;
    }

    dom.modal.setAttribute('aria-hidden', 'false');
    dom.modal.classList.add('mc-modal--open');

    // Save state before any navigation from the modal
    storage.save();
  }

  // ============================================
  // API Submission
  // ============================================
  async function handleSubmit() {
    if (!config.isLoggedIn) {
      window.location.href = config.loginUrl;
      return;
    }

    if (!validateSubmission()) return;

    state.isSubmitting = true;
    dom.submitBtn.disabled = true;
    dom.submitBtn.textContent = 'Submitting...';

    utils.hide(dom.errorContainer);
    utils.hide(dom.successContainer);

    try {
      const payload = buildPayload();
      const response = await submitQuote(payload);

      if (response.success) {
        showSuccess(response);
      } else {
        showError(response.error?.message || 'Failed to submit quote');
      }
    } catch (error) {
      console.error('Quote submission error:', error);
      showError(error.message || 'An unexpected error occurred');
    } finally {
      state.isSubmitting = false;
      dom.submitBtn.textContent = 'SUBMIT QUOTE REQUEST';
      updateSubmitButton();
    }
  }

  function validateSubmission() {
    // Ensure we're on the correct variant type for the mode
    if (config.mode === 'yardage') {
      if (!config.isYardVariant) {
        showError('Please select a yardage material before submitting your quote');
        return false;
      }
    } else {
      if (!config.isPanelVariant) {
        showError('Please select a panel material before submitting your quote');
        return false;
      }
    }

    if (!state.totals) {
      showError('Please confirm at least one wall');
      return false;
    }

    if (!state.cfa.type) {
      showError('Please select a CFA option');
      return false;
    }

    // Validate Photo CFA email
    if (state.cfa.type === 'photo') {
      const emailInput = root.querySelector('[data-mc-cfa-email]');
      if (!emailInput || !emailInput.value.trim()) {
        showError('Please enter an email address for the Photo CFA');
        return false;
      }
      if (!validateCfaEmail()) {
        showError('Please enter a valid email address for the Photo CFA');
        return false;
      }
    }

    // Validate CFA shipping if physical
    if (state.cfa.type === 'physical') {
      const requiredFields = ['name', 'address1', 'city', 'state', 'postal'];
      const fieldLabels = {
        name: 'Full Name',
        address1: 'Address Line 1',
        city: 'City',
        state: 'State',
        postal: 'ZIP Code'
      };

      for (const field of requiredFields) {
        const input = root.querySelector(`[data-mc-cfa-${field}]`);
        if (!input || !input.value.trim()) {
          showError(`CFA shipping: ${fieldLabels[field]} is required`);
          return false;
        }
      }

      // Validate ZIP code format
      if (!validateZipCode()) {
        showError('Please enter a valid ZIP code for the CFA shipping address');
        return false;
      }
    }

    return true;
  }

  function buildPayload() {
    const confirmedWalls = state.walls.filter(w => w.confirmed);

    // Build CFA data
    const cfaData = { type: state.cfa.type };
    if (state.cfa.type === 'photo') {
      cfaData.email = root.querySelector('[data-mc-cfa-email]')?.value || '';
    } else if (state.cfa.type === 'physical') {
      cfaData.shippingAddress = {
        name: root.querySelector('[data-mc-cfa-name]')?.value || '',
        company: root.querySelector('[data-mc-cfa-company]')?.value || '',
        address1: root.querySelector('[data-mc-cfa-address1]')?.value || '',
        address2: root.querySelector('[data-mc-cfa-address2]')?.value || '',
        city: root.querySelector('[data-mc-cfa-city]')?.value || '',
        state: root.querySelector('[data-mc-cfa-state]')?.value || '',
        postal: root.querySelector('[data-mc-cfa-postal]')?.value || '',
        country: 'US',
        phone: root.querySelector('[data-mc-cfa-phone]')?.value || ''
      };
    }

    // Build mode-specific payload
    if (config.mode === 'yardage') {
      const overage = yardageCalculator.calculateOverage(state.totals);

      // Build wallGroups array for backend schema
      const wallGroups = [];
      const groups = state.totals.groups || [];
      groups.forEach((group, idx) => {
        const wallIds = group.walls.map(w => w.number);
        let groupStrips = 0;
        let groupYards = 0;
        let groupSqFt = 0;
        group.walls.forEach(w => {
          groupStrips += (w.calculatedStrips || 0);
          groupYards += (w.calculatedYards || 0);
          groupSqFt += (w.calculatedSqFt || 0);
        });
        wallGroups.push({
          groupNumber: idx + 1,
          wallIds,
          totalStrips: groupStrips,
          totalYards: utils.ceil(groupYards),
          totalSqFt: Math.round(groupSqFt * 100) / 100
        });
      });

      return {
        productType: 'yardage',
        product: {
          id: config.productId,
          variantId: config.variantId,
          handle: config.productHandle,
          title: config.productTitle,
          sku: config.variantSku,
          materialCode: config.currentMaterialCode,
          materialName: getMaterialName(config.currentMaterialCode)
        },
        customer: {
          id: config.customerId,
          email: config.customerEmail,
          name: config.customerName
        },
        walls: confirmedWalls.map(w => ({
          number: w.number,
          heightFt: w.heightFt,
          heightIn: w.heightIn,
          widthFt: w.widthFt,
          widthIn: w.widthIn,
          continuesFromPrevious: w.continuesFromPrevious,
          confirmed: true,
          strips: w.calculatedStrips || 0,
          effectiveHeightInches: w.calculatedEffectiveHeight || 0,
          yardsPerStrip: w.calculatedYardsPerStrip || 0,
          totalYards: w.calculatedYards || 0,
          sqFt: w.calculatedSqFt || 0
        })),
        totals: {
          totalStrips: state.totals.totalStrips,
          totalYards: state.totals.totalYards,
          totalSqFt: state.totals.totalSqFt,
          bolts: state.totals.bolts,
          wallGroups: wallGroups
        },
        overage: {
          type: state.overage.type,
          additionalYards: overage.additionalYards,
          totalYards: overage.totalYards,
          totalSqFt: overage.totalSqFt
        },
        cfa: cfaData,
        projectDetails: {
          sidemark: state.projectDetails.sidemark,
          projectName: state.projectDetails.projectName,
          previousOrderNumber: state.projectDetails.previousOrder,
          notes: state.projectDetails.notes
        },
        metadata: {
          productType: 'yardage',
          materialWidthInches: config.yardageWidth,
          repeatHeightInches: config.repeatHeight,
          productionWidth: config.productionWidth,
          installationType: config.installationType,
          calculatorVersion: '2.0',
          source: 'storefront'
        }
      };
    }

    // Panel mode payload
    const overage = calculator.calculateOverage(state.totals);
    return {
      product: {
        id: config.productId,
        variantId: config.variantId,
        handle: config.productHandle,
        title: config.productTitle,
        sku: config.variantSku,
        materialCode: config.currentMaterialCode,
        materialName: getMaterialName(config.currentMaterialCode)
      },
      customer: {
        id: config.customerId,
        email: config.customerEmail,
        name: config.customerName
      },
      walls: confirmedWalls.map(w => ({
        number: w.number,
        heightFt: w.heightFt,
        heightIn: w.heightIn,
        widthFt: w.widthFt,
        widthIn: w.widthIn,
        widthInches: utils.feetInchesToInches(w.widthFt, w.widthIn),
        heightInches: utils.feetInchesToInches(w.heightFt, w.heightIn),
        continuesFromPrevious: w.continuesFromPrevious,
        confirmed: true,
        calculatedPanels: w.calculatedPanels,
        calculatedPanelHeight: w.calculatedPanelHeight,
        calculatedSqFt: w.calculatedSqFt,
        sequence: w.sequence,
        groupId: w.groupId
      })),
      totals: {
        totalPanels: state.totals.totalPanels,
        totalSqFt: state.totals.totalSqFt,
        tallestPanelHeight: state.totals.tallestPanelHeight,
        panelCounts: state.totals.panelCounts,
        breakdown: state.totals.breakdown
      },
      overage: {
        type: state.overage.type,
        totalPanels: overage.panels,
        totalSqFt: overage.sqFt,
        breakdown: overage.breakdown,
        counts: overage.counts || state.overage.customPanels
      },
      cfa: cfaData,
      projectDetails: {
        sidemark: state.projectDetails.sidemark,
        projectName: state.projectDetails.projectName,
        previousOrderNumber: state.projectDetails.previousOrder,
        notes: state.projectDetails.notes
      },
      metadata: {
        panelWidthInches: config.panelWidth,
        pattern: config.repeat,
        bleedInches: config.bleed,
        productionWidth: config.productionWidth,
        installationType: config.installationType,
        calculatorMode: 'panel',
        calculatorVersion: '2.0',
        source: 'storefront'
      }
    };
  }

  async function submitQuote(payload) {
    let lastError;

    for (let attempt = 1; attempt <= CONFIG.RETRY_ATTEMPTS; attempt++) {
      try {
        const response = await fetch(config.apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error?.message || `HTTP ${response.status}`);
        }

        return data;
      } catch (error) {
        lastError = error;
        console.warn(`Quote submit attempt ${attempt} failed:`, error);

        if (attempt < CONFIG.RETRY_ATTEMPTS) {
          await utils.delay(CONFIG.RETRY_DELAY * attempt);
        }
      }
    }

    throw lastError;
  }

  function showSuccess(response) {
    // Clear saved state on success
    storage.clear();

    // Update success modal content
    if (dom.successModalOrderName) {
      dom.successModalOrderName.textContent = response.draftOrder?.name || 'Submitted';
    }

    // Show the success modal (full-window popup)
    if (dom.successModal) {
      dom.successModal.setAttribute('aria-hidden', 'false');
      dom.successModal.classList.add('mc-modal--open');
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    }

    // Also update the inline success container for fallback
    if (dom.orderName) {
      dom.orderName.textContent = response.draftOrder?.name || 'Submitted';
    }
    utils.show(dom.successContainer);
  }

  function closeSuccessModal() {
    if (dom.successModal) {
      dom.successModal.setAttribute('aria-hidden', 'true');
      dom.successModal.classList.remove('mc-modal--open');
      document.body.style.overflow = ''; // Restore scroll
    }
  }

  function showError(message) {
    if (dom.errorMessage) {
      dom.errorMessage.textContent = message;
    }
    utils.show(dom.errorContainer);
  }

  // ============================================
  // State Restoration
  // ============================================
  function restoreState() {
    const saved = storage.load();
    if (!saved) return false;

    console.log('Restoring calculator state from sessionStorage', saved);

    // Restore state values
    state.wallCounter = saved.wallCounter || 0;
    state.overage = saved.overage || { type: 'standard', customPanels: { A: 0, B: 0, C: 0 } };
    state.cfa = saved.cfa || { type: null };
    state.projectDetails = saved.projectDetails || {
      sidemark: '',
      projectName: '',
      previousOrder: '',
      notes: ''
    };

    // Restore walls
    if (saved.walls && saved.walls.length > 0) {
      saved.walls.forEach((savedWall, index) => {
        if (index === 0) {
          // Add first wall normally, then update it
          wallManager.addWall({
            heightFt: savedWall.heightFt,
            heightIn: savedWall.heightIn,
            widthFt: savedWall.widthFt,
            widthIn: savedWall.widthIn
          });

          // Get the wall element and update select values
          const wallEl = dom.wallsContainer.querySelector('[data-mc-wall]');
          if (wallEl) {
            updateWallInputs(wallEl, savedWall);

            // If wall was confirmed, confirm it
            if (savedWall.confirmed) {
              const wall = state.walls[0];
              if (wall) {
                wall.heightFt = savedWall.heightFt;
                wall.heightIn = savedWall.heightIn;
                wall.widthFt = savedWall.widthFt;
                wall.widthIn = savedWall.widthIn;
                wallManager.validateWall(wallEl, wall);
                wallManager.updateWallPreview(wallEl, wall);

                // Trigger confirm
                const confirmBtn = wallEl.querySelector('[data-wall-confirm]');
                if (confirmBtn && !confirmBtn.disabled) {
                  confirmBtn.click();
                }
              }
            }
          }
        } else {
          // Add additional walls
          wallManager.addWall({
            heightFt: savedWall.heightFt,
            heightIn: savedWall.heightIn,
            widthFt: savedWall.widthFt,
            widthIn: savedWall.widthIn
          });

          const wallEls = dom.wallsContainer.querySelectorAll('[data-mc-wall]');
          const wallEl = wallEls[index];
          const wall = state.walls[index];

          if (wallEl && wall) {
            updateWallInputs(wallEl, savedWall);

            // Set continues from previous
            const continuesCheckbox = wallEl.querySelector('[data-wall-continues]');
            if (continuesCheckbox) {
              continuesCheckbox.checked = savedWall.continuesFromPrevious;
              wall.continuesFromPrevious = savedWall.continuesFromPrevious;
            }

            // If wall was confirmed, confirm it
            if (savedWall.confirmed) {
              wall.heightFt = savedWall.heightFt;
              wall.heightIn = savedWall.heightIn;
              wall.widthFt = savedWall.widthFt;
              wall.widthIn = savedWall.widthIn;
              wallManager.validateWall(wallEl, wall);
              wallManager.updateWallPreview(wallEl, wall);

              const confirmBtn = wallEl.querySelector('[data-wall-confirm]');
              if (confirmBtn && !confirmBtn.disabled) {
                confirmBtn.click();
              }
            }
          }
        }
      });
    }

    // Restore overage UI
    const overageRadio = root.querySelector(`[data-mc-overage-type][value="${state.overage.type}"]`);
    if (overageRadio) {
      overageRadio.checked = true;
      if (state.overage.type === 'custom') {
        const overageCustom = root.querySelector('[data-mc-overage-custom]');
        utils.show(overageCustom);
        ['A', 'B', 'C'].forEach(letter => {
          const input = root.querySelector(`[data-mc-overage-${letter.toLowerCase()}]`);
          if (input && state.overage.customPanels[letter]) {
            input.value = state.overage.customPanels[letter];
          }
        });
      }
    }

    // Restore CFA UI
    if (state.cfa.type) {
      const cfaRadio = root.querySelector(`[data-mc-cfa-type][value="${state.cfa.type}"]`);
      if (cfaRadio) {
        cfaRadio.checked = true;
        // Trigger change to show/hide appropriate sections
        cfaRadio.dispatchEvent(new Event('change'));
      }
    }

    // Restore project details
    const sidemarkInput = root.querySelector('[data-mc-sidemark]');
    const projectNameInput = root.querySelector('[data-mc-project-name]');
    const previousOrderInput = root.querySelector('[data-mc-previous-order]');
    const notesInput = root.querySelector('[data-mc-notes]');

    if (sidemarkInput) sidemarkInput.value = state.projectDetails.sidemark || '';
    if (projectNameInput) projectNameInput.value = state.projectDetails.projectName || '';
    if (previousOrderInput) previousOrderInput.value = state.projectDetails.previousOrder || '';
    if (notesInput) notesInput.value = state.projectDetails.notes || '';

    // Restore accordion state
    if (saved.accordionOpen && dom.accordion) {
      dom.accordion.setAttribute('aria-expanded', 'true');
      dom.accordionContent.setAttribute('aria-hidden', 'false');
    }

    return true;
  }

  function updateWallInputs(wallEl, savedWall) {
    const heightFtSelect = wallEl.querySelector('[data-wall-height-ft]');
    const heightInSelect = wallEl.querySelector('[data-wall-height-in]');
    const widthFtSelect = wallEl.querySelector('[data-wall-width-ft]');
    const widthInSelect = wallEl.querySelector('[data-wall-width-in]');

    if (heightFtSelect) heightFtSelect.value = String(savedWall.heightFt || 0);
    if (heightInSelect) heightInSelect.value = String(savedWall.heightIn || 0);
    if (widthFtSelect) widthFtSelect.value = String(savedWall.widthFt || 0);
    if (widthInSelect) widthInSelect.value = String(savedWall.widthIn || 0);
  }

  // ============================================
  // Initialize
  // ============================================
  function init() {
    setupEventListeners();

    // Try to restore saved state first
    const restored = restoreState();

    // If no saved state, add first wall
    if (!restored) {
      wallManager.addWall();
    }

    // Auto-open accordion if hash matches or returning from login
    if (window.location.hash === '#calculator' || restored) {
      if (dom.accordion) {
        dom.accordion.setAttribute('aria-expanded', 'true');
        dom.accordionContent.setAttribute('aria-hidden', 'false');
      }
    }

    // Save state periodically and on visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        storage.save();
      }
    });

    // Save before unload
    window.addEventListener('beforeunload', () => {
      storage.save();
    });

    // Listen for Pipeline theme variant changes
    // When variant changes dynamically, reload page to get correct Liquid-rendered content
    document.addEventListener('theme:variant:change', function(event) {
      const variant = event.detail.variant;
      // Only reload if we have a calculator on page and variant changed
      if (variant && root) {
        // Save current calculator state before reload
        storage.save();
        // Reload page with new variant URL to get correct Liquid-rendered content
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        url.hash = 'calculator'; // Keep accordion open after reload
        window.location.href = url.toString();
      }
    });

    console.log('Mural Calculator initialized', { config, dom, restored });
  }

  // Start
  init();

})();
